import type { ReportEmployer, ReportSubOwner } from '../../../../../../types';
import type { UserAccessesListSelectedData } from './UserAccessesList.types';

/**
 * Helper to convert employers to object format
 */
export const getEmployersAsObject = (
  employers: string | Record<string, string | Record<string, string>> | undefined,
): Record<string, string> => {
  if (employers === '*' || typeof employers === 'string') return {};
  return (employers as Record<string, string>) || {};
};

/**
 * Helper to convert subOwners to object format
 */
export const getSubOwnersAsObject = (
  subOwners: string | Record<string, string | Record<string, string>> | undefined,
): Record<string, string> => {
  if (subOwners === '*' || typeof subOwners === 'string') return {};
  return (subOwners as Record<string, string>) || {};
};

/**
 * Check if a subOwner is selected
 */
export const isSubOwnerSelected = (
  selectedData: UserAccessesListSelectedData | undefined,
  subOwnerId: string,
): boolean => {
  const subOwners = selectedData?.subOwners;
  if (subOwners === '*') return true;
  if (typeof subOwners === 'object') return !!subOwners?.[subOwnerId];
  return false;
};

/**
 * Check if an employer is selected
 */
export const isEmployerSelected = (
  selectedData: UserAccessesListSelectedData | undefined,
  employerId: string,
): boolean => {
  const employers = selectedData?.employers;
  if (employers === '*') return true;
  if (typeof employers === 'object') return !!employers?.[employerId];
  return false;
};

/**
 * Get all employer IDs from a subOwner (including subEmployers)
 */
export const getAllEmployerIdsFromSubOwner = (subOwner: ReportSubOwner): string[] => {
  const ids: string[] = [];
  subOwner.employers?.forEach(employer => {
    ids.push(employer.employerId);
    employer.subEmployers?.forEach(subEmp => ids.push(subEmp.employerId));
  });
  return ids;
};

/**
 * Get employer and its subEmployer IDs
 */
export const getEmployerWithSubEmployerIds = (employer: ReportEmployer): string[] => {
  const ids = [employer.employerId];
  employer.subEmployers?.forEach(subEmp => ids.push(subEmp.employerId));
  return ids;
};

/**
 * Handle selecting/unselecting a SubOwner (parent)
 * - When checking: checks the SubOwner and all its Employers + SubEmployers
 * - When unchecking: unchecks the SubOwner AND all its children (Employers + SubEmployers)
 */
export const handleSelectSubOwner = (
  currentSelectedData: UserAccessesListSelectedData,
  subOwner: ReportSubOwner,
): UserAccessesListSelectedData => {
  const isCurrentlySelected = isSubOwnerSelected(currentSelectedData, subOwner.subOwnerId);
  const newSubOwners = { ...getSubOwnersAsObject(currentSelectedData.subOwners) };
  const newEmployers = { ...getEmployersAsObject(currentSelectedData.employers) };
  let newEventFlows = [...(currentSelectedData.eventFlows || [])];

  if (isCurrentlySelected) {
    delete newSubOwners[subOwner.subOwnerId];
    const employerIdsToRemove = getAllEmployerIdsFromSubOwner(subOwner);
    employerIdsToRemove.forEach(id => {
      delete newEmployers[id];
    });
    newEventFlows = newEventFlows.filter(id => !employerIdsToRemove.includes(id));
  } else {
    newSubOwners[subOwner.subOwnerId] = '*';
    subOwner.employers?.forEach(employer => {
      newEmployers[employer.employerId] = '*';
      employer.subEmployers?.forEach(subEmp => {
        newEmployers[subEmp.employerId] = '*';
      });
    });
  }

  return {
    ...currentSelectedData,
    areAllSubOwnersSelected: false,
    subOwners: newSubOwners,
    employers: newEmployers,
    eventFlows: newEventFlows,
  };
};

/**
 * Handle selecting/unselecting an Employer under a SubOwner
 * - When checking: checks the Employer, its SubEmployers, AND the parent SubOwner
 * - When unchecking: only unchecks the Employer and its SubEmployers (parent remains)
 */
export const handleSelectEmployerUnderSubOwner = (
  currentSelectedData: UserAccessesListSelectedData,
  subOwner: ReportSubOwner,
  employer: ReportEmployer,
): UserAccessesListSelectedData => {
  const isCurrentlySelected = isEmployerSelected(currentSelectedData, employer.employerId);
  const newSubOwners = { ...getSubOwnersAsObject(currentSelectedData.subOwners) };
  const newEmployers = { ...getEmployersAsObject(currentSelectedData.employers) };
  let newEventFlows = [...(currentSelectedData.eventFlows || [])];

  if (isCurrentlySelected) {
    const idsToRemove = getEmployerWithSubEmployerIds(employer);
    idsToRemove.forEach(id => {
      delete newEmployers[id];
    });
    newEventFlows = newEventFlows.filter(id => !idsToRemove.includes(id));
  } else {
    newSubOwners[subOwner.subOwnerId] = '*';
    newEmployers[employer.employerId] = '*';
    employer.subEmployers?.forEach(subEmp => {
      newEmployers[subEmp.employerId] = '*';
    });
  }

  return {
    ...currentSelectedData,
    areAllSubOwnersSelected: false,
    subOwners: newSubOwners,
    employers: newEmployers,
    eventFlows: newEventFlows,
  };
};

/**
 * Handle selecting/unselecting a SubEmployer under a SubOwner
 * - When checking: checks the SubEmployer, parent Employer, AND grandparent SubOwner
 * - When unchecking: only unchecks the SubEmployer (parents remain)
 */
export const handleSelectSubEmployerUnderSubOwner = (
  currentSelectedData: UserAccessesListSelectedData,
  subOwner: ReportSubOwner,
  parentEmployer: ReportEmployer,
  subEmployerId: string,
): UserAccessesListSelectedData => {
  const isCurrentlySelected = isEmployerSelected(currentSelectedData, subEmployerId);
  const newSubOwners = { ...getSubOwnersAsObject(currentSelectedData.subOwners) };
  const newEmployers = { ...getEmployersAsObject(currentSelectedData.employers) };
  let newEventFlows = [...(currentSelectedData.eventFlows || [])];

  if (isCurrentlySelected) {
    delete newEmployers[subEmployerId];
    newEventFlows = newEventFlows.filter(id => id !== subEmployerId);
  } else {
    newSubOwners[subOwner.subOwnerId] = '*';
    newEmployers[parentEmployer.employerId] = '*';
    newEmployers[subEmployerId] = '*';
  }

  return {
    ...currentSelectedData,
    areAllSubOwnersSelected: false,
    subOwners: newSubOwners,
    employers: newEmployers,
    eventFlows: newEventFlows,
  };
};

/**
 * Handle selecting/unselecting a top-level Employer (from employers list)
 * - When checking: checks the Employer AND all its SubEmployers
 * - When unchecking: unchecks the Employer AND all its SubEmployers
 */
export const handleSelectTopLevelEmployer = (
  currentSelectedData: UserAccessesListSelectedData,
  employer: ReportEmployer,
): UserAccessesListSelectedData => {
  const isCurrentlySelected = isEmployerSelected(currentSelectedData, employer.employerId);
  const newEmployers = { ...getEmployersAsObject(currentSelectedData.employers) };
  let newEventFlows = [...(currentSelectedData.eventFlows || [])];

  if (isCurrentlySelected) {
    const idsToRemove = getEmployerWithSubEmployerIds(employer);
    idsToRemove.forEach(id => {
      delete newEmployers[id];
    });
    newEventFlows = newEventFlows.filter(id => !idsToRemove.includes(id));
  } else {
    newEmployers[employer.employerId] = '*';
    employer.subEmployers?.forEach(subEmp => {
      newEmployers[subEmp.employerId] = '*';
    });
  }

  return {
    ...currentSelectedData,
    areAllEmployersSelected: false,
    employers: newEmployers,
    eventFlows: newEventFlows,
  };
};

/**
 * Handle selecting/unselecting a SubEmployer under a top-level Employer
 * - When checking: checks the SubEmployer AND the parent Employer
 * - When unchecking: only unchecks the SubEmployer (parent remains)
 */
export const handleSelectSubEmployerUnderTopLevelEmployer = (
  currentSelectedData: UserAccessesListSelectedData,
  parentEmployer: ReportEmployer,
  subEmployerId: string,
): UserAccessesListSelectedData => {
  const isCurrentlySelected = isEmployerSelected(currentSelectedData, subEmployerId);
  const newEmployers = { ...getEmployersAsObject(currentSelectedData.employers) };
  let newEventFlows = [...(currentSelectedData.eventFlows || [])];

  if (isCurrentlySelected) {
    delete newEmployers[subEmployerId];
    newEventFlows = newEventFlows.filter(id => id !== subEmployerId);
  } else {
    newEmployers[parentEmployer.employerId] = '*';
    newEmployers[subEmployerId] = '*';
  }

  return {
    ...currentSelectedData,
    areAllEmployersSelected: false,
    employers: newEmployers,
    eventFlows: newEventFlows,
  };
};

/**
 * Handle select all SubOwners
 */
export const handleSelectAllSubOwners = (
  currentSelectedData: UserAccessesListSelectedData,
  subOwners: ReportSubOwner[] | undefined,
  topLevelEmployerIdsSet: Set<string>,
): UserAccessesListSelectedData => {
  const shouldSelectAll = currentSelectedData.subOwners !== '*';

  if (shouldSelectAll) {
    return {
      ...currentSelectedData,
      areAllSubOwnersSelected: true,
      subOwners: '*',
      eventFlows: [],
    };
  } else {
    const currentEmployers = getEmployersAsObject(currentSelectedData.employers);
    const newEmployers: Record<string, string> = {};

    const preserveAllTopLevel = currentSelectedData.areAllEmployersSelected;

    if (preserveAllTopLevel) {
      topLevelEmployerIdsSet.forEach(employerId => {
        newEmployers[employerId] = '*';
      });
    } else {
      Object.keys(currentEmployers).forEach(employerId => {
        if (topLevelEmployerIdsSet.has(employerId)) {
          newEmployers[employerId] = currentEmployers[employerId];
        }
      });
    }

    return {
      ...currentSelectedData,
      areAllSubOwnersSelected: false,
      subOwners: {},
      employers: newEmployers,
      eventFlows: [],
    };
  }
};

/**
 * Handle select all SubOwners on current page
 */
export const handleSelectAllSubOwnersOnPage = (
  currentSelectedData: UserAccessesListSelectedData,
  subOwners: ReportSubOwner[] | undefined,
  topLevelEmployerIdsSet: Set<string>,
): UserAccessesListSelectedData => {
  if (!subOwners) return currentSelectedData;

  const allOnPageSelected = subOwners.every(subOwner => isSubOwnerSelected(currentSelectedData, subOwner.subOwnerId));

  const shouldSelectAllOnPage = !allOnPageSelected;

  const newSubOwners = { ...getSubOwnersAsObject(currentSelectedData.subOwners) };
  const newEmployers = { ...getEmployersAsObject(currentSelectedData.employers) };
  let newEventFlows = [...(currentSelectedData.eventFlows || [])];

  if (shouldSelectAllOnPage) {
    subOwners.forEach(subOwner => {
      newSubOwners[subOwner.subOwnerId] = '*';
      subOwner.employers?.forEach(employer => {
        newEmployers[employer.employerId] = '*';
        employer.subEmployers?.forEach(subEmp => {
          newEmployers[subEmp.employerId] = '*';
        });
      });
    });
  } else {
    subOwners.forEach(subOwner => {
      delete newSubOwners[subOwner.subOwnerId];
      const employerIds = getAllEmployerIdsFromSubOwner(subOwner);
      employerIds.forEach(employerId => {
        if (!topLevelEmployerIdsSet.has(employerId)) {
          delete newEmployers[employerId];
          newEventFlows = newEventFlows.filter(id => id !== employerId);
        }
      });
    });
  }

  return {
    ...currentSelectedData,
    areAllSubOwnersSelected: false,
    subOwners: newSubOwners,
    employers: newEmployers,
    eventFlows: newEventFlows,
  };
};

/**
 * Get employer IDs that are selected through SubOwners
 */
export const getEmployerIdsFromSelectedSubOwners = (
  subOwners: ReportSubOwner[] | undefined,
  currentSubOwnersSelection: string | Record<string, string | Record<string, string>> | undefined,
): Set<string> => {
  const employerIds = new Set<string>();
  if (!subOwners) return employerIds;

  const selectedSubOwnerIds =
    currentSubOwnersSelection === '*'
      ? subOwners.map(so => so.subOwnerId)
      : Object.keys((currentSubOwnersSelection as Record<string, string>) || {});

  subOwners.forEach(subOwner => {
    if (selectedSubOwnerIds.includes(subOwner.subOwnerId)) {
      subOwner.employers?.forEach(employer => {
        employerIds.add(employer.employerId);
        employer.subEmployers?.forEach(subEmp => employerIds.add(subEmp.employerId));
      });
    }
  });

  return employerIds;
};

/**
 * Handle select all Employers
 */
export const handleSelectAllEmployers = (
  currentSelectedData: UserAccessesListSelectedData,
  subOwners: ReportSubOwner[] | undefined,
): UserAccessesListSelectedData => {
  const shouldSelectAll = currentSelectedData.employers !== '*';

  if (shouldSelectAll) {
    return {
      ...currentSelectedData,
      areAllEmployersSelected: true,
      employers: '*',
      eventFlows: [],
    };
  } else {
    const employerIdsFromSelectedSubOwners = getEmployerIdsFromSelectedSubOwners(
      subOwners,
      currentSelectedData.subOwners,
    );
    const newEmployers: Record<string, string> = {};

    employerIdsFromSelectedSubOwners.forEach(employerId => {
      newEmployers[employerId] = '*';
    });

    return {
      ...currentSelectedData,
      areAllEmployersSelected: false,
      employers: newEmployers,
      eventFlows: [],
    };
  }
};

/**
 * Handle select all Employers on current page
 */
export const handleSelectAllEmployersOnPage = (
  currentSelectedData: UserAccessesListSelectedData,
  employers: ReportEmployer[] | undefined,
  subOwners: ReportSubOwner[] | undefined,
): UserAccessesListSelectedData => {
  if (!employers) return currentSelectedData;

  const allOnPageSelected = employers.every(employer => isEmployerSelected(currentSelectedData, employer.employerId));

  const shouldSelectAllOnPage = !allOnPageSelected;

  const newEmployers = { ...getEmployersAsObject(currentSelectedData.employers) };
  let newEventFlows = [...(currentSelectedData.eventFlows || [])];

  if (shouldSelectAllOnPage) {
    employers.forEach(employer => {
      newEmployers[employer.employerId] = '*';
      employer.subEmployers?.forEach(subEmp => {
        newEmployers[subEmp.employerId] = '*';
      });
    });
  } else {
    const employerIdsFromSelectedSubOwners = getEmployerIdsFromSelectedSubOwners(
      subOwners,
      currentSelectedData.subOwners,
    );

    employers.forEach(employer => {
      const idsToRemove = getEmployerWithSubEmployerIds(employer);
      idsToRemove.forEach(id => {
        if (!employerIdsFromSelectedSubOwners.has(id)) {
          delete newEmployers[id];
          newEventFlows = newEventFlows.filter(flowId => flowId !== id);
        }
      });
    });
  }

  return {
    ...currentSelectedData,
    areAllEmployersSelected: false,
    employers: newEmployers,
    eventFlows: newEventFlows,
  };
};
